import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Navbar from './Navbar';
import Footer from './Footer';
import GolferSidebar from './GolferSidebar';
import GolferBottomNav from '../golfer/GolferBottomNav';
import MarketingCtaBar from './MarketingCtaBar';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';

// Native Pro Capacitor shell (com.golfauth.pro) has no consumer
// marketing surface, so the sticky "Build my bag" bar must never appear
// there. Static per-session check, safe at module scope.
const IS_NATIVE = Capacitor.isNativePlatform();

export default function Layout() {
  const location = useLocation();
  const { customer, professional, isInitialized } = useAuthStore();

  // Hash-scroll bridge. React Router's <Link to="/path#anchor"> updates
  // the URL but does NOT trigger native hash-anchor scrolling the way a
  // raw <a href> does. Without this hook the navbar Pricing link (and
  // any future /…#section link) would land on the page with the hash
  // in the URL but no scroll. We listen on every location change and,
  // if a hash is present, scroll the matching element into view.
  //
  // Two timing details:
  //   • requestAnimationFrame ensures the target element exists in the
  //     DOM before we measure it (esp. for cross-page navigations
  //     where the destination component is lazy-loaded).
  //   • behavior: 'smooth' so the user perceives the scroll instead
  //     of an abrupt jump. CSS `scroll-mt-*` on the target compensates
  //     for the fixed navbar height — see PricingMatrix `id="pricing"`.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.pathname, location.hash]);

  // Scroll-to-top on route change. Without this, navigating from a page
  // scrolled halfway down (e.g. /golfer/current-quotes) into a new page
  // lands the user mid-content because the browser preserves scroll
  // position across SPA navigations. Skipped when a hash is present —
  // the hash-scroll bridge above handles that case.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);

  // Customer-facing routes that should render the golfer sidebar
  // for signed-in customers. /offer/:code and /checkout/:code live
  // outside the /golfer/* prefix but are part of the same customer
  // journey — the sidebar should follow them through so they can
  // still navigate to their bag / quotes / orders mid-purchase.
  // Founder report 2026-06-26: "from this screen there is no left
  // nav bar."
  const isGolferContextRoute =
    location.pathname.startsWith('/golfer') ||
    location.pathname.startsWith('/offer') ||
    location.pathname.startsWith('/checkout');
  const showGolferSidebar = customer && isGolferContextRoute;

  // Marketing routes own their own hero / banner composition (often
  // a brand-blue strip flush to the navbar followed by a navy hero).
  // The universal main-pt-* below would push that whole composition
  // down and break the "navbar → banner" continuity. Skip the
  // universal padding on these routes; the page itself controls
  // top spacing. Keep this list in sync with Navbar.tsx.
  const MARKETING_EXACT_ROUTES = new Set(['/']);
  const MARKETING_PREFIX_ROUTES = [
    '/about',
    '/pro',
    '/find-professional',
    '/contact',
    '/stats',
    '/glossary',
    '/verification-101',
    '/privacy',
    '/terms',
    '/security',
    '/cookies',
    '/delete-account',
    '/team',
  ];
  const isMarketingRoute =
    MARKETING_EXACT_ROUTES.has(location.pathname) ||
    MARKETING_PREFIX_ROUTES.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );

  // Footer is marketing chrome (privacy, terms, social, find-a-pro). It
  // makes sense for anonymous visitors who arrive on / or /pro and might
  // need it. Once a user is signed in (golfer OR pro) they have their
  // own in-app navigation and the footer just eats screen space below
  // their actual workflow — particularly cramped on mobile.
  //
  // `isInitialized` gates the footer during the auth-bootstrap window:
  // before the store hydrates, customer + professional are both null
  // even when the user *is* signed in, so we'd flash the marketing
  // footer beneath the GolfAuthLoadingScreen on the golfer routes the
  // Core iOS app hands off to (and worse, the footer would squeeze the
  // loader's available height so it'd sit above page-centre instead of
  // dead middle). Treat unresolved auth as "could be signed in" and
  // wait for isInitialized before deciding.
  // The redesigned homepage and Pro page are full-bleed premium takeovers:
  // each renders its own sticky nav and footer inside the page, so the
  // global Layout chrome (fixed Navbar, its top offset, the marketing
  // footer, and the mobile CTA bar) must be suppressed on those routes to
  // avoid a stacked double nav. Every other route keeps the standard chrome.
  // Trailing-slash tolerant — `/pro/` must not reintroduce the fixed Navbar.
  const isPremiumMarketing =
    location.pathname === '/' ||
    location.pathname === '/pro' ||
    location.pathname === '/pro/';

  const showFooter = isInitialized && !customer && !professional && !isPremiumMarketing;

  // Sticky mobile signup bar: same audience as the footer (anon, auth
  // resolved), but scoped to marketing routes and web-only. Gives mobile
  // visitors an always-visible primary CTA the hamburger navbar can't.
  const showMarketingCtaBar =
    isInitialized &&
    !customer &&
    !professional &&
    !IS_NATIVE &&
    isMarketingRoute &&
    !isPremiumMarketing;

  return (
    <div className="flex flex-col min-h-screen">
      {!isPremiumMarketing && <Navbar />}
      {/* Top padding offsets the fixed navbar. Includes
          env(safe-area-inset-top) so content sits below the navbar
          even after the navbar grows by the iPhone notch / dynamic
          island inset under Capacitor's `contentInset: 'never'`.
          On web (and Android, where the inset is 0) this resolves
          to plain 4rem / 5rem — same behaviour as before. The premium
          homepage renders its own sticky nav, so it opts out of the
          fixed-navbar offset entirely. */}
      <div
        className={cn(
          'flex flex-1 min-h-0',
          !isPremiumMarketing &&
            'pt-[calc(4rem+env(safe-area-inset-top,0px))] md:pt-[calc(5rem+env(safe-area-inset-top,0px))]',
        )}
      >
        {showGolferSidebar && <GolferSidebar />}
        <main className={cn(
          "flex-grow min-w-0 flex flex-col min-h-0",
          // Universal breathing room between the navbar bottom edge
          // and page content on every in-app route. The parent div
          // already offsets the exact navbar height via
          // pt-[calc(4rem+env(…))]; this adds a visible gap on top of
          // that so back links, section eyebrows, and headers don't
          // sit flush against the navy navbar boundary. Marketing
          // routes opt out — their heroes / brand banners are designed
          // to sit flush against the navbar.
          !isMarketingRoute && "pt-5 sm:pt-6",
          showGolferSidebar ? "overflow-hidden md:overflow-auto" : "",
          showGolferSidebar ? "pb-16 md:pb-0" : "",
          // Clear the sticky mobile CTA bar so it never covers the last
          // slice of page content (footer links, final CTA microcopy).
          showMarketingCtaBar && "pb-24 md:pb-0",
        )}>
          <Outlet />
        </main>
        {showGolferSidebar && <GolferBottomNav />}
      </div>
      {showFooter && <Footer />}
      {showMarketingCtaBar && <MarketingCtaBar />}
    </div>
  );
}